import { useEffect, useMemo, useState } from 'react';
import { Banner, Spinner } from './States';
import { UpgradeCard } from './UpgradeCard';
import { useInventario } from '../hooks/useInventario';
import type { InventoryItem } from '../api/types';

const brl = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Cotação mínima para a peça aparecer na oferta.
 *
 * Inventário de CS2 é cheio de peça de centavos, e elas dominavam a lista:
 * o cliente rolava dezenas de cartões de R$ 0,15 para achar o que tem valor
 * de troca. Abaixo deste piso a peça não muda a conta, só atrapalha a escolha.
 */
const PISO_OFERTA_BRL = 1;

interface Props {
  selecionados: string[];
  onChange: (assetIds: string[]) => void;
  /** O crédito sobe para a página: é o teto do "cabe no meu crédito". */
  onTotal: (totalBrl: number) => void;
  recolhido: boolean;
  onRecolher: () => void;
}

/**
 * A coluna da esquerda: o que o cliente entrega.
 *
 * O total fica no cabeçalho, e não no rodapé, porque é o número que o cliente
 * consulta o tempo todo enquanto escolhe do outro lado — no fim da lista ele
 * sumiria assim que a rolagem começasse.
 */
export function UpgradeOffer({ selecionados, onChange, onTotal, recolhido, onRecolher }: Props) {
  const { user, carregandoConta, loginUrl, data, erro, carregando, carregar } = useInventario();
  const [soNegociaveis, setSoNegociaveis] = useState(true);

  /**
   * Peças de cotação baixa demais para valerem a rolagem.
   *
   * Só vale para quem tem cotação: item sem cotação é outro assunto, e quem
   * manda nele é a caixa "Só com cotação".
   */
  const abaixoDoPiso = useMemo(
    () => (data ? data.items.filter((i) => i.quoteBrl !== null && i.quoteBrl <= PISO_OFERTA_BRL).length : 0),
    [data],
  );

  const visiveis = useMemo(() => {
    if (!data) return [];
    const acimaDoPiso = data.items.filter((i) => i.quoteBrl === null || i.quoteBrl > PISO_OFERTA_BRL);
    return soNegociaveis ? acimaDoPiso.filter((i) => i.quoteBrl !== null) : acimaDoPiso;
  }, [data, soNegociaveis]);

  const total = useMemo(() => {
    if (!data) return 0;
    const escolhidos = new Set(selecionados);
    return data.items.reduce((s, i) => (escolhidos.has(i.assetId) ? s + (i.quoteBrl ?? 0) : s), 0);
  }, [data, selecionados]);

  const mercadoSelecionado = useMemo(() => {
    if (!data) return 0;
    const escolhidos = new Set(selecionados);
    return data.items.reduce((s, i) => (escolhidos.has(i.assetId) ? s + (i.refPriceBrl ?? 0) : s), 0);
  }, [data, selecionados]);

  useEffect(() => {
    onTotal(total);
  }, [total, onTotal]);

  function alternar(item: InventoryItem) {
    if (item.quoteBrl === null) return;
    onChange(
      selecionados.includes(item.assetId)
        ? selecionados.filter((id) => id !== item.assetId)
        : [...selecionados, item.assetId],
    );
  }

  return (
    <section className={`panel up-coluna ${recolhido ? 'fechada' : ''}`}>
      <header className="up-cabeca">
        <button className="up-chevron" onClick={onRecolher} title={recolhido ? 'Abrir' : 'Recolher'}>
          {recolhido ? '›' : '‹'}
        </button>
        <span className="up-cabeca-titulo">Você Oferece</span>
        <span className="up-cabeca-total">
          {brl(total)} <i>🛒</i>
        </span>
      </header>

      {!recolhido && (
        <>
          {erro && <Banner kind="error">{erro}</Banner>}
          {carregandoConta && <Spinner label="Carregando a sua conta…" />}
          {carregando && !data && <Spinner label="Lendo o seu inventário na Steam…" />}

          {!carregandoConta && !user && (
            <div className="up-vazio">
              <h2>Sua Oferta</h2>
              <p>Adicione os itens que deseja trocar</p>

              {/* O bloco só existe enquanto não há conta: é o único momento em
                  que a tela não tem nada de útil para mostrar, e explicar a
                  troca aqui aproveita o espaço em vez de deixá-lo vazio. */}
              <div className="up-heroi">
                <span className="up-selo-login">Login Steam necessário</span>
                <strong>Troque seus itens CS2 instantaneamente</strong>
                <p>
                  Entre com a Steam para lermos o seu inventário. Cada item vira crédito na hora, e o crédito paga
                  a skin que você escolher do outro lado.
                </p>
                <i className="up-seta">↓</i>
                <a className="btn primary" href={loginUrl}>
                  Entrar via Steam
                </a>
              </div>
            </div>
          )}

          {user?.needsOnboarding && (
            <div className="up-vazio">
              <h2>Sua Oferta</h2>
              <Banner kind="warn">
                Falta o seu trade link. É por ele que a negociação acontece — sem isso não dá para cotar.
              </Banner>
              <a className="btn primary" href="/cadastro">
                Completar cadastro
              </a>
            </div>
          )}

          {data && (
            <>
              <div className="up-barra">
                <label className="check inline">
                  <input
                    type="checkbox"
                    checked={soNegociaveis}
                    onChange={(e) => setSoNegociaveis(e.target.checked)}
                  />
                  <span>Só com cotação</span>
                </label>
                <button className="btn ghost small" onClick={() => onChange([])} disabled={selecionados.length === 0}>
                  Limpar
                </button>
                <button className="btn ghost small" onClick={() => void carregar(true)} disabled={carregando}>
                  ↻
                </button>
              </div>

              {/* O desconto continua à vista: o cliente precisa saber que a
                  entrada é avaliada abaixo do mercado antes de escolher. */}
              <p className="up-nota">
                {data.negociaveis} de {data.total} itens com cotação · avaliados {data.discountPct}% abaixo do
                mercado de origem
                {/* O que o piso tirou fica dito: lista que encolhe sem explicar
                    parece inventário faltando. */}
                {abaixoDoPiso > 0 &&
                  ` · ${abaixoDoPiso} ${abaixoDoPiso === 1 ? 'peça' : 'peças'} de até ${brl(PISO_OFERTA_BRL)} fora da lista`}
                {selecionados.length > 0 && ` · mercado da seleção ${brl(mercadoSelecionado)}`}
              </p>

              {/* A rolagem é da lista, não da página: o total do cabeçalho
                  precisa continuar visível enquanto o cliente escolhe. */}
              <div className="up-rolagem">
                {visiveis.length === 0 ? (
                  <div className="up-vazio">
                    <h2>Sua Oferta</h2>
                    <p>
                      Nenhum item com cotação acima de {brl(PISO_OFERTA_BRL)}. Peças de menor valor, travadas por
                      troca ou fora do nosso catálogo não entram.
                    </p>
                  </div>
                ) : (
                  <div className="up-grid">
                    {visiveis.map((item) => (
                      <UpgradeCard
                        key={item.assetId}
                        preco={brl(item.quoteBrl)}
                        nome={item.name}
                        imagem={item.iconUrl}
                        desgaste={item.exterior}
                        statTrak={/stattrak/i.test(item.name)}
                        selecionado={selecionados.includes(item.assetId)}
                        bloqueado={item.quoteBrl === null}
                        motivo={item.motivo}
                        rodape={
                          item.quoteBrl === null ? null : (
                            <span className="dim">mercado {brl(item.refPriceBrl)}</span>
                          )
                        }
                        onClick={() => alternar(item)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
